import { Cell, Color, BotDifficulty, COLORS, GameState, PlayerState, RingSize, SIZES } from './types';

const LINES: [number, number, number][] = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
  [0, 3, 6], [1, 4, 7], [2, 5, 8], // cols
  [0, 4, 8], [2, 4, 6],             // diagonals
];

function makeCell(): Cell {
  return { small: null, medium: null, large: null };
}

export function createGame(playerConfigs: { name: string; color: Color; isBot: boolean; botDifficulty: BotDifficulty | null; playerId?: number }[]): GameState {
  const players: PlayerState[] = playerConfigs.map((cfg, i) => ({
    color: cfg.color,
    name: cfg.name,
    pieces: { small: 3, medium: 3, large: 3 },
    isBot: cfg.isBot,
    botDifficulty: cfg.botDifficulty,
    playerId: cfg.playerId ?? i,
  }));

  return {
    board: Array.from({ length: 9 }, makeCell),
    players,
    currentPlayerIndex: 0,
    winner: null,
    winLine: null,
  };
}

export function canPlace(board: Cell[], cellIndex: number, size: RingSize): boolean {
  return board[cellIndex][size] === null;
}

export function hasValidMove(board: Cell[], player: PlayerState): boolean {
  for (const size of SIZES) {
    if (player.pieces[size] > 0) {
      for (let i = 0; i < 9; i++) {
        if (canPlace(board, i, size)) return true;
      }
    }
  }
  return false;
}

export function isGameOver(state: GameState): boolean {
  return state.players.every(p => !hasValidMove(state.board, p));
}

export function placePiece(state: GameState, cellIndex: number, size: RingSize): GameState {
  const player = state.players[state.currentPlayerIndex];

  if (!canPlace(state.board, cellIndex, size)) return state;
  if (player.pieces[size] <= 0) return state;

  const newBoard = state.board.map((cell, i) =>
    i === cellIndex ? { ...cell, [size]: player.color } : cell
  );

  const newPlayers = state.players.map((p, i) =>
    i === state.currentPlayerIndex
      ? { ...p, pieces: { ...p.pieces, [size]: p.pieces[size] - 1 } }
      : p
  );

  const { winner, winLine } = checkWin(newBoard);

  const nextIndex = (state.currentPlayerIndex + 1) % state.players.length;

  return {
    board: newBoard,
    players: newPlayers,
    currentPlayerIndex: winner ? state.currentPlayerIndex : nextIndex,
    winner,
    winLine,
  };
}

export function advanceToNextValidPlayer(state: GameState): GameState {
  let idx = state.currentPlayerIndex;
  for (let i = 0; i < state.players.length; i++) {
    const candidate = (idx + i) % state.players.length;
    if (hasValidMove(state.board, state.players[candidate])) {
      return { ...state, currentPlayerIndex: candidate };
    }
  }
  return state;
}

function checkWin(board: Cell[]): { winner: Color | null; winLine: number[] | null } {
  for (const line of LINES) {
    const [a, b, c] = line;

    // Win type 1: same size, same color across 3 cells in a line
    for (const size of SIZES) {
      const col = board[a][size];
      if (col && col === board[b][size] && col === board[c][size]) {
        return { winner: col, winLine: [...line] };
      }
    }

    // Win type 3: staircase — ascending small→medium→large or descending
    for (const color of COLORS) {
      if (
        board[a].small === color &&
        board[b].medium === color &&
        board[c].large === color
      ) {
        return { winner: color, winLine: [...line] };
      }
      if (
        board[a].large === color &&
        board[b].medium === color &&
        board[c].small === color
      ) {
        return { winner: color, winLine: [...line] };
      }
    }
  }

  // Win type 2: all three sizes in one cell (tower)
  for (let i = 0; i < 9; i++) {
    const cell = board[i];
    const col = cell.small;
    if (col && col === cell.medium && col === cell.large) {
      return { winner: col, winLine: [i] };
    }
  }

  return { winner: null, winLine: null };
}
